import {HttpStatusCode} from '@angular/common/http';
import {Component, ViewChild} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {ActivatedRoute, Params} from '@angular/router';
import {
    AccountStatus,
    DocumentAttachment,
    EntityType,
    Participant,
    ReferenceBookResponse,
    ReferenceBookType,
    RefundApiService,
    RefundResponse,
    RefundStatus,
    RefundType,
    RejectType,
} from '@npck/af-frontend-api';
import {LocalizationService, RouterService, rxSpinner, Spinner, TAB} from '@npck/libs/commons';
import {DialogService, SnackbarService} from '@npck/libs/ui-kit';
import {combineLatest, EMPTY, mergeMap, Observable, of} from 'rxjs';
import {DeleteDialogComponent, DeleteDialogData} from '../../../components/delete-dialog/delete-dialog.component';
import {AttachmentForm} from '../../../components/files/file/file.component';
import {createAttachmentsRequest} from '../../../components/files/files.component';
import {AuthHolderService} from '../../../domain/auth-holder.service';
import {NESTED_NESTED_TAB} from '../../../domain/constants';
import {DictionaryHolderService} from '../../../domain/dictionary-holder.service';
import {INCIDENT_ID, REFUND_ID} from '../../../domain/query-params.keys';
import {MainContainerComponent} from '../../../layout/main-container/main-container.component';
import {Path} from '../../../path';
import {FINAL_REFUND_STATUSES, getRefundStatusInfo, RefundsTab, RefundStatusInfo} from '../refunds.component';
import {RefundExecutionDialogComponent} from './refund-execution-dialog/refund-execution-dialog.component';
import {
    RefundRejectDialogComponent,
    RefundRejectDialogData,
    RefundRejectFormValue,
} from './refund-reject-dialog/refund-reject-dialog.component';

@Component({
    selector: 'af-refund-details',
    standalone: false,
    templateUrl: './refund-details.component.html',
})
export class RefundDetailsComponent {
    @ViewChild('mainContainerComponent') mainContainerComponent: MainContainerComponent;
    Path = Path;
    EntityType = EntityType;
    Spinner = Spinner;
    statusInfo: RefundStatusInfo;
    refundId: string;
    incidentId: string;
    refund: RefundResponse;
    tabs: RefundDetailsTab[] = ['tabRefundDetails', 'tabHistory'];
    activeTab: RefundDetailsTab;
    backToTab: RefundsTab;
    selectedTabIndex: number;
    actionButtonLabel: string;
    hasDeleteButton: boolean;
    hasEditButton: boolean;
    hasRejectButton: boolean;

    private statusDictionary: ReferenceBookResponse[];
    private countryDictionary: ReferenceBookResponse[];
    private genderDictionary: ReferenceBookResponse[];
    private currencyDictionary: ReferenceBookResponse[];
    private accountStatusDictionary: ReferenceBookResponse[];

    constructor(
        private authHolderService: AuthHolderService,
        private activatedRoute: ActivatedRoute,
        private dialogService: DialogService,
        private routerService: RouterService,
        private refundApiService: RefundApiService,
        private localizationService: LocalizationService,
        private dictionaryHolderService: DictionaryHolderService,
        private snackbarService: SnackbarService,
    ) {
        this.incidentId = this.activatedRoute.snapshot.paramMap.get(INCIDENT_ID);
        this.refundId = this.activatedRoute.snapshot.paramMap.get(REFUND_ID);

        this.backToTab = this.activatedRoute.snapshot.queryParamMap.get(TAB) as RefundsTab;
        const nestedTab = this.activatedRoute.snapshot.queryParamMap.get(NESTED_NESTED_TAB) as RefundDetailsTab;
        this.activeTab = this.tabs.includes(nestedTab) ? nestedTab : 'tabRefundDetails';
        this.selectedTabIndex = this.tabs.findIndex(tab => tab == this.activeTab);
        this.handleNavigation(this.selectedTabIndex);
        combineLatest([
            this.refundApiService.getRefund(this.refundId),
            this.dictionaryHolderService.getReference(ReferenceBookType.REFUND_STATUS),
            this.dictionaryHolderService.getReference(ReferenceBookType.COUNTRY),
            this.dictionaryHolderService.getReference(ReferenceBookType.GENDER),
            this.dictionaryHolderService.getReference(ReferenceBookType.CURRENCY),
            this.dictionaryHolderService.getReference(ReferenceBookType.ACCOUNT_STATUS),
        ])
            .pipe(rxSpinner('initial'))
            .subscribe({
                next: ([response, statusDictionary, countryDict, genderDict, currencyDict, accountStatusDict]) => {
                    this.statusDictionary = statusDictionary;
                    this.countryDictionary = countryDict;
                    this.genderDictionary = genderDict;
                    this.currencyDictionary = currencyDict;
                    this.accountStatusDictionary = accountStatusDict;
                    this.setRefund(response);
                },
                error: error => {
                    if (error.status === HttpStatusCode.Forbidden) {
                        this.routerService.navigate(Path.REFUNDS);
                        this.snackbarService.showSnackBar('i18n_af_snackBarMessageRefundAccessRestricted', 'error', false,
                            {refundId: this.refundId});
                        return;
                    }
                    throw error;
                },
            });
    }

    breadcrumbCustomizer = (path: string): string => {
        if (this.incidentId === path) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#IncidentId', {[INCIDENT_ID]: this.incidentId});
        }
        if (this.refundId === path) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#RefundId', {[REFUND_ID]: this.refundId});
        }
        return path;
    };

    navigateBack = (): Observable<boolean> => {
        if (this.incidentId) {
            return of(true);
        }
        this.routerService.navigateToTab<RefundsTab>(Path.REFUNDS, this.backToTab);
        return of(false);
    };

    handleNavigation($event: number): void {
        this.activeTab = this.tabs[$event];
        if (this.incidentId) {
            this.routerService.navigate(
                Path.INCIDENTS_REFUND_DETAILS,
                {
                    pathParams: {
                        [INCIDENT_ID]: this.incidentId,
                        [REFUND_ID]: this.refundId,
                    },
                    queryParams: {[NESTED_NESTED_TAB]: this.activeTab},
                    queryParamsHandling: 'merge',
                });

        } else {
            this.routerService.navigate(
                Path.REFUND_DETAILS,
                {
                    pathParams: {[REFUND_ID]: this.refundId},
                    queryParams: {[NESTED_NESTED_TAB]: this.activeTab},
                    queryParamsHandling: 'merge',
                });
        }
    }

    editRefund(): void {
        const pathParams: Params = {
            [REFUND_ID]: this.refundId,
        };
        if (this.incidentId) {
            pathParams[INCIDENT_ID] = this.incidentId;
        }
        let path = Path.REFUND_EDIT;
        if (this.incidentId) {
            path = Path.INCIDENTS_REFUND_EDIT;
        }
        this.routerService.navigate(path, {
            pathParams: pathParams,
            queryParamsHandling: 'merge',
        });
    }

    deleteRefund(): void {
        this.dialogService.openCustomDialog<DeleteDialogComponent, DeleteDialogResult>(
            DeleteDialogComponent,
            <DeleteDialogData>{
                id: this.refundId,
                title: 'i18n_af_deleteRefundDialogTitle',
                subtitle: 'i18n_af_deleteRefundDialogSubtitle',
            },
        )
            .pipe(
                mergeMap(result => !!result ? this.refundApiService.deleteRefund(this.refundId, result.comment) : EMPTY),
            )
            .subscribe(() => {
                this.snackbarService
                    .showSnackBar('i18n_af_snackBarMessageRefundDeleted', 'success', false, {[REFUND_ID]: this.refundId});
                this.mainContainerComponent.breadcrumbs.navigateBack();
            });
    }

    openRejectDialog(): void {
        this.dialogService.openCustomDialog<RefundRejectDialogComponent, RefundRejectFormValue>(
            RefundRejectDialogComponent,
            <RefundRejectDialogData>{refund: this.refund},
        )
            .subscribe(dialogResult => {
                if (!!dialogResult) {
                    this.submit(RefundStatus.REJECTED, dialogResult);
                }
            });
    }

    changeRefundStatus(): void {
        const processingBin = this.refund.processingBin;
        const isProcessingOnMySide = processingBin === this.authHolderService.organizationBin;
        if (isProcessingOnMySide && this.refund.type === RefundType.PROSECUTOR_ORDER && this.refund.initiatorBin === processingBin) {
            this.dialogService.openDialog({
                title: 'i18n_af_dialogSendToProsecutorConfirmationDialogTitle',
                subtitle: 'i18n_af_dialogSendToProsecutorConfirmationDialogSubtitle',
                buttonOkText: 'i18n_af_buttonSignAndSend',
                buttonCancelText: 'i18n_af_buttonCancel',
            })
                .subscribe(result => {
                    if (result) {
                        this.submit(RefundStatus.PENDING_APPROVAL);
                    }
                });

        } else if (isProcessingOnMySide
            && this.refund.type === RefundType.VOLUNTARY
            && this.refund.initiatorBin === processingBin
            && [RefundStatus.DRAFT, RefundStatus.REJECTED].includes(this.refund.status)) {
            this.submit(RefundStatus.IN_EXECUTION);
        } else if (isProcessingOnMySide && this.refund.prosecutorOfficeBin === processingBin) {
            this.dialogService.openDialog({
                title: 'i18n_af_dialogApprovalConfirmationDialogTitle',
                subtitle: 'i18n_af_dialogApprovalConfirmationDialogSubtitle',
                buttonOkText: 'i18n_af_buttonApprovalAndSign',
                buttonCancelText: 'i18n_af_buttonCancel',
            })
                .subscribe(result => {
                    if (result) {
                        this.submit(RefundStatus.IN_EXECUTION);
                    }
                });
        } else if (isProcessingOnMySide && this.refund.senderBin === processingBin && this.refund.status === RefundStatus.IN_EXECUTION) {
            this.dialogService.openCustomDialogFitContent<RefundExecutionDialogComponent, FormGroup<AttachmentForm>>(
                RefundExecutionDialogComponent,
                null,
                {autoFocus: false},
            )
                .subscribe(result => {
                    if (!!result) {
                        this.submit(
                            RefundStatus.PENDING_CONFIRMATION,
                            null,
                            createAttachmentsRequest({attachments: [result.getRawValue()]}),
                        );
                    }
                });
        } else if (isProcessingOnMySide
            && this.refund.receiverBin === processingBin
            && this.refund.status === RefundStatus.PENDING_CONFIRMATION
        ) {
            this.dialogService.openDialog({
                title: 'i18n_af_dialogRefundConfirmationDialogTitle',
                subtitle: 'i18n_af_dialogRefundConfirmationDialogSubtitle',
                buttonOkText: 'i18n_af_buttonConfirmRefund',
                buttonCancelText: 'i18n_af_buttonCancel',
                translationParams: {
                    id: this.refund.id,
                },
            })
                .subscribe(result => {
                    if (result) {
                        this.submit(RefundStatus.EXECUTED);
                    }
                });
        }
    }

    private setRefund(response: RefundResponse): void {
        this.statusInfo = getRefundStatusInfo(this.statusDictionary, response, this.localizationService);
        this.tabs = response.attachments.some(attachment => attachment.docType === 'PROSECUTOR_ORDER')
            ? ['tabRefundDetails', 'tabOrder', 'tabHistory']
            : ['tabRefundDetails', 'tabHistory'];
        this.refund = response;
        this.setupButtons();
    }

    private submit(
        status: RefundStatus = RefundStatus.DRAFT,
        refundRejectFormValue?: RefundRejectFormValue,
        paymentOrderFormValue?: DocumentAttachment[],
    ): void {
        this.refundApiService.updateRefund(this.refund.id, {
                ...this.refund,
                sender: this.normalizeParticipant(this.refund.sender),
                receiver: this.normalizeParticipant(this.refund.receiver),
                status: status,
                isStatusChange: this.refund.status !== status,
                comment: refundRejectFormValue?.comment,
                rejectType: refundRejectFormValue?.reason as RejectType,
                attachments: [...this.refund.attachments, ...(paymentOrderFormValue ?? [])],
                frontendHost: window.location.host,
            },
        )
            .subscribe({
                next: (response) => {
                    if (response.url) {
                        window.location.href = response.url;
                    } else {
                        this.setRefund(response);
                    }
                },
                error: err => console.log(err),
            });
    }

    private normalizeParticipant(participant: Participant): Participant {
        if (!participant) {
            return null;
        }
        return {
            ...participant,
            country: this.resolveCode(this.countryDictionary, participant.country),
            info: participant.info ? {
                ...participant.info,
                gender: this.resolveCode(this.genderDictionary, participant.info.gender),
            } : participant.info,
            account: participant.account ? {
                ...participant.account,
                currency: this.resolveCode(this.currencyDictionary, participant.account.currency),
                status: this.resolveCode(this.accountStatusDictionary, participant.account.status) as AccountStatus,
            } : participant.account,
        };
    }

    private resolveCode(dictionary: ReferenceBookResponse[], value: string): string | null {
        if (!value) {
            return null;
        }
        const match = dictionary.find(item =>
            [item.code, item.code1, item.code2, item.name_ru].includes(value));
        return match?.code ?? null;
    }

    private setupButtons(): void {
        const processingBin = this.refund.processingBin;
        const isProcessingOnMySide = processingBin === this.authHolderService.organizationBin;
        this.actionButtonLabel = null;
        this.hasEditButton = false;
        this.hasRejectButton = false;
        const hasNpckAdminRole = this.authHolderService.hasAccess(['npck-admin']);
        this.hasDeleteButton = this.authHolderService.hasAccess(['npck-admin'])
            && this.refund.status !== RefundStatus.REJECTED
            && this.refund.status !== RefundStatus.DELETED;
        this.hasEditButton = hasNpckAdminRole && !FINAL_REFUND_STATUSES.includes(this.refund.status);

        if (!isProcessingOnMySide || FINAL_REFUND_STATUSES.includes(this.refund.status)) {
            return;
        }

        this.hasEditButton = [RefundStatus.DRAFT, RefundStatus.IN_EXECUTION].includes(this.refund.status);
        this.hasRejectButton = ![RefundStatus.PENDING_APPROVAL, RefundStatus.REJECTED].includes(this.refund.status);
        if (this.refund.status === RefundStatus.DRAFT && this.refund.initiatorBin === processingBin) {
            this.actionButtonLabel = this.refund.type === RefundType.VOLUNTARY ? 'i18n_af_buttonToExecution' : 'i18n_af_buttonToApproval';
            this.hasDeleteButton = this.refund.status === RefundStatus.DRAFT;
            this.hasRejectButton = false;
        }

        if (this.refund.status === RefundStatus.PENDING_APPROVAL && this.refund.prosecutorOfficeBin === processingBin) {
            this.actionButtonLabel = 'i18n_af_buttonApproval';
            this.hasRejectButton = true;
        }

        if (this.refund.status === RefundStatus.IN_EXECUTION && this.refund.senderBin === processingBin) {
            this.actionButtonLabel = 'i18n_af_buttonToConfirmation';
            this.hasRejectButton = true;
        }

        if (this.refund.status === RefundStatus.PENDING_CONFIRMATION && this.refund.receiverBin === processingBin) {
            this.actionButtonLabel = 'i18n_af_buttonExecute';
            this.hasRejectButton = true;
        }
    }
}

export type RefundDetailsTab = 'tabRefundDetails' | 'tabOrder' | 'tabHistory';

interface DeleteDialogResult {
    comment: string;
    reason: string | null;
}
